import React, { forwardRef, useState } from 'react';
import { NumericInput } from './NumericInput';

interface ClampedIntegerInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'inputMode' | 'pattern' | 'value' | 'onChange' | 'min' | 'max'> {
  /** Gjeldende (gyldige) verdi — eies av parent som tall. */
  value: number;
  /** Kalles med hvert gyldige tall innenfor [min, max] mens operatøren skriver. */
  onValueChange: (value: number) => void;
  min?: number;
  max?: number;
}

/**
 * Heltallsfelt med tak, f.eks. antall kopier, som kan tømmes mens man skriver (TW 36410006).
 *
 * Mønsteret `onChange={e => set(Math.max(1, parseInt(e.target.value) || 1))}` på et kontrollert
 * felt setter verdien tilbake til minimum i samme øyeblikk feltet blir tomt. På PC merkes det
 * ikke — man markerer tallet og skriver over, eller bruker spinnpilene. På nettbrett finnes
 * verken spinnpiler eller enkel tekstmarkering, så 1-tallet kunne ikke byttes ut.
 *
 * Her holdes et utkast mens feltet redigeres: gyldige tall sendes til parent med en gang,
 * tomt/ugyldig utkast vises som det er og faller tilbake til siste gyldige verdi ved blur.
 * Et trykk i feltet markerer hele tallet, så neste siffer erstatter det.
 */
export const ClampedIntegerInput = forwardRef<HTMLInputElement, ClampedIntegerInputProps>(
  function ClampedIntegerInput({ value, onValueChange, min = 1, max, onFocus, onBlur, ...props }, ref) {
    const [draft, setDraft] = useState<string | null>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      // NumericInput (integer) har allerede fjernet alt som ikke er sifre
      const raw = e.target.value;
      const parsed = parseInt(raw, 10);
      if (Number.isNaN(parsed)) {
        setDraft(raw);
        return;
      }
      if (max !== undefined && parsed > max) {
        setDraft(String(max));
        onValueChange(max);
        return;
      }
      setDraft(raw);
      if (parsed >= min) onValueChange(parsed);
    };

    return (
      <NumericInput
        ref={ref}
        mode="integer"
        maxLength={max !== undefined ? String(max).length : undefined}
        {...props}
        value={draft ?? String(value)}
        onChange={handleChange}
        onFocus={(e) => {
          e.target.select();
          onFocus?.(e);
        }}
        onBlur={(e) => {
          setDraft(null);
          onBlur?.(e);
        }}
      />
    );
  }
);
